import { cn } from "@istari-frontend/shared";
import { Tooltip, TooltipContent, TooltipTrigger } from "@istari-frontend/ui";
import { V3Models } from "@istari/istari-client";
import { AlertTriangle, ChevronDown, ChevronUp, X, XCircle } from "lucide-react";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { usePatchModelControlTags } from "~/api/useControlTags";
import { useAssignInfosecLevel } from "~/api/useInfosecLevels";
import { BarsRotateFade } from "~/assets/svg";
import { FileTypeIcon } from "~/components/file-type-icon";

import { useCreateResource } from "./use-create-resource";

type FileWithMetadata = {
  displayName: string;
  extension: string;
  externalIdentifier: string;
  file: File;
  tags: string[];
  resourceType: V3Models.ResourceTypeDto;
  infosecLevelId?: string | null;
};

type FileUploadState = "pending" | "uploading" | "success" | "error";

type UploadingFile = FileWithMetadata & {
  id: string;
  state: FileUploadState;
  serverFileId?: string;
  resourceId?: string;
  fileRevisionId?: string;
  errorMessage?: string;
};

type UploadProgress = {
  files: UploadingFile[];
  completedCount: number;
  totalCount: number;
  overallState: "uploading" | "success" | "error" | "partial";
};

type UseUploadResourceOptions = {
  onComplete?: (result: { fileIds: string[]; resourceIds: string[]; files: UploadingFile[] }) => void;
  onError?: (error: Error, file: FileWithMetadata) => void;
};

let activeToastId: string | number | null = null;
let activeProgress: UploadProgress | null = null;
let isCollapsed = false;

function generateId(): string {
  return Math.random().toString(36).substring(2, 11);
}

function deriveOverallState(files: UploadingFile[]): UploadProgress["overallState"] {
  const hasInFlight = files.some((f) => f.state === "uploading" || f.state === "pending");
  if (hasInFlight) return "uploading";
  const successCount = files.filter((f) => f.state === "success").length;
  const errorCount = files.filter((f) => f.state === "error").length;
  if (errorCount === 0) return "success";
  if (successCount === 0) return "error";
  return "partial";
}

function buildProgress(files: UploadingFile[]): UploadProgress {
  const completedCount = files.filter((f) => f.state === "success" || f.state === "error").length;
  return {
    files,
    completedCount,
    totalCount: files.length,
    overallState: deriveOverallState(files),
  };
}

function renderToast(progress: UploadProgress) {
  if (!activeToastId) return;
  const collapsed = isCollapsed;
  toast.custom(() => <ToastContent progress={progress} collapsed={collapsed} />, {
    id: activeToastId,
    duration: Infinity,
  });
}

function setProgress(progress: UploadProgress) {
  if (!activeToastId) return;
  activeProgress = progress;
  renderToast(progress);
}

function clearActiveToast() {
  if (activeToastId) toast.dismiss(activeToastId);
  activeToastId = null;
  activeProgress = null;
  isCollapsed = false;
}

export function resetUploadStateForTests() {
  activeToastId = null;
  activeProgress = null;
  isCollapsed = false;
}

export function useUploadResource(options: UseUploadResourceOptions = {}) {
  const { onComplete, onError } = options;

  const createResource = useCreateResource();
  const patchModelControlTags = usePatchModelControlTags();
  const assignModelInfosecLevel = useAssignInfosecLevel();

  const uploadSingleFile = useCallback(
    async (file: UploadingFile): Promise<UploadingFile> => {
      try {
        const data: V3Models.ResourceDto = await createResource.mutateAsync({
          file: file.file,
          displayName: file.displayName,
          description: "",
          externalIdentifier: file.externalIdentifier,
          resourceType: file.resourceType,
        });

        const serverFileId = data.fileId || data.resourceId;

        if (file.tags?.length > 0) {
          patchModelControlTags.mutate({
            modelId: data.resourceId,
            controlTagIdsActionTypes: file.tags.map((tagId) => ({ ids: [tagId], action: "set" as const })),
          });
        }

        if (file.infosecLevelId) {
          assignModelInfosecLevel.mutate({
            type: "model",
            id: data.resourceId,
            infosecLevelId: file.infosecLevelId,
          });
        }

        return {
          ...file,
          state: "success",
          serverFileId,
          resourceId: data.resourceId,
          fileRevisionId: data.fileRevisionId,
        };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Upload failed";
        onError?.(error instanceof Error ? error : new Error(errorMessage), file);
        return {
          ...file,
          state: "error",
          errorMessage,
        };
      }
    },
    [createResource, patchModelControlTags, assignModelInfosecLevel, onError],
  );

  const uploadFiles = useCallback(
    async (filesWithMetadata: FileWithMetadata[]) => {
      if (filesWithMetadata.length === 0) return;

      const newFiles: UploadingFile[] = filesWithMetadata.map((file) => ({
        ...file,
        id: generateId(),
        state: "pending" as const,
      }));
      const newIds = new Set(newFiles.map((f) => f.id));

      if (!activeToastId) {
        activeToastId = generateId();
        isCollapsed = false;
      }

      const startingFiles = activeProgress ? [...activeProgress.files, ...newFiles] : newFiles;
      setProgress(buildProgress(startingFiles));

      const uploadPromises = newFiles.map(async (file) => {
        const updatedFiles = (activeProgress?.files ?? []).map((f) =>
          f.id === file.id ? ({ ...f, state: "uploading" } as UploadingFile) : f,
        );
        setProgress(buildProgress(updatedFiles));

        const result = await uploadSingleFile({ ...file, state: "uploading" });

        const finalFiles = (activeProgress?.files ?? []).map((f) => (f.id === file.id ? result : f));
        setProgress(buildProgress(finalFiles));

        return result;
      });

      const completedFiles = await Promise.all(uploadPromises);
      const sessionResults = completedFiles.filter((f) => newIds.has(f.id));

      const fileIds = sessionResults.filter((f) => f.serverFileId).map((f) => f.serverFileId!);
      const resourceIds = sessionResults.filter((f) => f.resourceId).map((f) => f.resourceId!);

      if (fileIds.length > 0) {
        onComplete?.({ fileIds, resourceIds, files: sessionResults });
      }
    },
    [uploadSingleFile, onComplete],
  );

  const dismissToast = useCallback(() => {
    clearActiveToast();
  }, []);

  return { uploadFiles, dismissToast };
}

function ToastContent({ progress, collapsed }: { progress: UploadProgress; collapsed: boolean }) {
  const { files, completedCount, totalCount, overallState } = progress;

  return (
    <div
      className="bg-popover text-popover-foreground border-border flex w-80 flex-col gap-2 rounded-lg border p-3 shadow-lg"
      data-testid="upload-progress-toast"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2 pl-1">
          <StatusIcon state={overallState} />
          <span className="truncate text-sm font-medium tabular-nums">
            {getHeaderText(overallState, totalCount, completedCount)}
          </span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={() => {
              isCollapsed = !isCollapsed;
              if (activeProgress) renderToast(activeProgress);
            }}
            className="text-muted-foreground hover:text-foreground hover:bg-accent flex size-7 items-center justify-center rounded transition-colors duration-200 ease-out"
            aria-label={collapsed ? "Expand uploads" : "Collapse uploads"}
          >
            {collapsed ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          </button>
          <button
            type="button"
            onClick={clearActiveToast}
            className="text-muted-foreground hover:text-foreground hover:bg-accent flex size-7 items-center justify-center rounded transition-colors duration-200 ease-out"
            aria-label="Dismiss"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="max-h-48 space-y-0.5 overflow-y-auto">
          {files.map((file) => (
            <FileRow key={file.id} file={file} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatusIcon({ state }: { state: UploadProgress["overallState"] | FileUploadState }) {
  if (state === "uploading" || state === "pending") {
    return <BarsRotateFade className="fill-primary size-4 flex-shrink-0" />;
  }

  if (state === "success") {
    return <SuccessCheck className="size-4 flex-shrink-0" />;
  }

  if (state === "error") {
    return <XCircle className="text-destructive size-4 flex-shrink-0" />;
  }

  if (state === "partial") {
    return <AlertTriangle className="text-warning size-4 flex-shrink-0" />;
  }

  return null;
}

function FileRow({ file }: { file: UploadingFile }) {
  const navigate = useNavigate();
  const isClickable = file.state === "success" && Boolean(file.resourceId);

  const handleNavigate = () => {
    if (!isClickable || !file.resourceId) return;
    navigate(`/files/${file.resourceId}`);
  };

  return (
    <div
      className={cn("group/row flex items-center gap-2 rounded px-2 py-1.5 text-xs", isClickable && "hover:bg-accent")}
      onClick={handleNavigate}
      onKeyDown={(e) => {
        if (isClickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleNavigate();
        }
      }}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <FileTypeIcon extension={file.extension} className="text-foreground size-4 flex-shrink-0" />
      <span className="min-w-0 flex-1 truncate">
        <span className="text-foreground">{file.displayName}</span>
        <span className="text-muted-foreground">.{file.extension}</span>
      </span>
      {file.state === "error" && file.errorMessage && (
        <span className="text-muted-foreground flex-shrink-0 truncate text-[10px]" title={file.errorMessage}>
          Failed
        </span>
      )}
      <span className="relative flex size-4 flex-shrink-0 items-center justify-center">
        {isClickable ? (
          <>
            <span
              className="absolute inset-0 flex items-center justify-center transition-opacity duration-150 ease-[cubic-bezier(0.2,0,0,1)] group-hover/row:opacity-0"
              aria-hidden="true"
            >
              <FileStateIcon state={file.state} />
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="text-muted-foreground absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 ease-[cubic-bezier(0.2,0,0,1)] group-hover/row:opacity-100"
                  aria-label="Go to this file"
                >
                  <FileTypeIcon extension={file.extension} className="size-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="left">Go to this file</TooltipContent>
            </Tooltip>
          </>
        ) : (
          <FileStateIcon state={file.state} />
        )}
      </span>
    </div>
  );
}

function FileStateIcon({ state }: { state: FileUploadState }) {
  if (state === "pending") {
    return <span className="text-muted-foreground inline-block size-3 rounded-full border border-current" />;
  }

  if (state === "uploading") {
    return <BarsRotateFade className="fill-primary size-3" />;
  }

  if (state === "success") {
    return <SuccessCheck className="size-4" />;
  }

  if (state === "error") {
    return <XCircle className="text-destructive size-4" />;
  }

  return null;
}

function SuccessCheck({ className }: { className?: string }) {
  return (
    <span className={cn("bg-success flex items-center justify-center rounded-full", className)} aria-hidden="true">
      <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 16 16" fill="none">
        <path
          d="M3 8L6.5 11.5L13 5"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function getHeaderText(state: UploadProgress["overallState"], totalCount: number, completedCount: number): string {
  switch (state) {
    case "uploading":
      return `Uploading ${completedCount} of ${totalCount}…`;
    case "success":
      return `${totalCount} upload${totalCount === 1 ? "" : "s"} complete`;
    case "error":
      return `Failed to upload ${totalCount} file${totalCount === 1 ? "" : "s"}`;
    case "partial":
      return `${completedCount} of ${totalCount} uploaded`;
    default:
      return "";
  }
}
